import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  ArrowLeft,
  CheckCircle,
  Lock,
  User,
  Grid,
  Video,
  Image,
  Link,
  MapPin,
  Mail,
  Phone,
  Clock,
  Heart,
  MessageCircle,
  Share2,
  Calendar,
  Eye,
  Users,
  UserPlus,
  ExternalLink,
  Play,
} from "lucide-react";
import { useProfileCompleteQuery } from "@/hooks/use-profile-query";
import { StalkerModal } from "./components/stalker-modal";
import { StoryCard } from "./components/story-card";
import { LoadingSpinner } from "./components/loading-spinner";
import ChatPlaceloader from "./components/ChatPlaceloader";
import { resolveStoryImage } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { FloatingSupport } from "./components/FloatingSupport";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
);

interface ExtendedProfileData {
  profile_pic_url_hd?: string;
  profile_pic_url: string;
  has_highlight_reels?: boolean;
  is_verified?: boolean;
  full_name: string;
  username: string;
  is_private?: boolean;
  media_count?: number;
  follower_count?: number;
  following_count?: number;
  biography?: string;
  bio_links?: Array<{ url: string; title?: string }>;
  public_email?: string;
  public_phone_number?: string;
  public_phone_country_code?: string;
  location_data?: {
    city_name?: string;
    zip?: string;
  };
  total_igtv_videos?: number;
  latest_reel_media?: number;
  followersCount?: number;
  followingCount?: number;
}

export function DeliveryProfilePage() {
  const { username = "" } = useParams();
  const navigate = useNavigate();
  const [stalkerModalOpen, setStalkerModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("posts");
  const [showAllBioLinks, setShowAllBioLinks] = useState(false);
  const [viewingStory, setViewingStory] = useState<number | null>(null);

  const {
    data: profileData,
    isLoading,
    isError,
    error,
  } = useProfileCompleteQuery(username, "search");

  // Helper para formatear números con sufijos K, M
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  // Configurar datos de gráfico para estadísticas de seguidores
  const followerDistributionData = {
    labels: ["Seguidores", "No Seguidores"],
    datasets: [
      {
        data: [75, 25], // Valores predeterminados, se calcularían con datos reales
        backgroundColor: ["#ef8d02", "#02b4ef"],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    cutout: "70%",
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  // Datos del gráfico de métricas de engagement
  const engagementData = {
    labels: ["Me gusta", "Comentarios", "Compartidos", "Guardados"],
    datasets: [
      {
        label: "Promedio por publicación",
        data: [350, 78, 45, 120],
        backgroundColor: "#ef8d02",
        borderRadius: 6,
      },
    ],
  };

  const engagementOptions = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
        ticks: {
          color: "#aaaaaa",
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#aaaaaa",
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-black">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (isError || !profileData) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-black text-white p-6">
        <h1 className="text-2xl font-bold mb-4">Error al cargar el perfil</h1>
        <p className="text-neutral-400 mb-6">
          {error?.message || "No fue posible cargar los datos del perfil"}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="bg-neutral-800 hover:bg-neutral-700 text-white px-6 py-3 rounded-full flex items-center"
        >
          <ArrowLeft className="mr-2" size={18} />
          Volver
        </button>
      </div>
    );
  }

  const {
    profileData: profile,
    feedMedia,
    followersAndStories,
  } = profileData as {
    profileData: ExtendedProfileData;
    feedMedia: { items: any[] };
    followersAndStories: {
      data?: { items?: any[] };
      stories?: { items: any[] };
    };
  };

  // Calcular cuántos seguidores tienen stories
  const followersWithStories =
    followersAndStories?.data?.items?.filter(
      (follower) => follower.latest_story_ts !== null,
    ).length || 0;

  // Obtener datos de stories
  const storyItems = followersAndStories?.stories?.items || [];

  // Dividir medios en diferentes tipos para pestañas
  const posts = feedMedia?.items || [];
  const videoItems = posts.filter((item) => item?.media_type === 2);
  const photoItems = posts.filter((item) => item?.media_type === 1);
  const taggedItems = posts.filter((item) => item?.user_tags?.length > 0);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container max-w-[800px] mx-auto px-4 py-6">
        {/* Encabezado con botón de regreso e información del perfil */}
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 hover:bg-neutral-800 p-2 rounded-full transition"
          >
            <ArrowLeft className="h-6 w-6 text-neutral-300" />
          </button>

          <div className="flex items-center">
            <div className="relative mr-6">
              <img
                src={profile.profile_pic_url_hd || profile.profile_pic_url}
                alt={profile.username}
                className="rounded-full w-24 h-24 object-cover border-2 border-neutral-700"
              />
              {profile.has_highlight_reels && (
                <div
                  className="absolute inset-0 rounded-full border-2 border-gradient-to-br from-yellow-500 via-orange-500 to-pink-500"
                  style={{ padding: "3px" }}
                ></div>
              )}
              {profile.is_verified && (
                <div className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-1">
                  <CheckCircle size={16} className="text-white" />
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center">
                <h1 className="text-2xl font-bold">{profile.full_name}</h1>
                {profile.is_verified && (
                  <CheckCircle size={16} className="text-blue-500 ml-2" />
                )}
              </div>
              <span className="text-neutral-400 text-sm">
                @{profile.username}
              </span>
              {profile.is_private && (
                <div className="flex items-center text-neutral-400 mt-1">
                  <Lock size={14} className="mr-1" />
                  <span className="text-sm">Cuenta privada</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Estadísticas del perfil */}
        <div className="flex justify-between mb-8 text-center">
          <div className="flex-1">
            <div className="text-2xl font-bold">
              {formatNumber(profile.media_count || 0)}
            </div>
            <div className="text-sm text-neutral-400">Publicaciones</div>
          </div>
          <div className="flex-1">
            <div className="text-2xl font-bold">
              {formatNumber(profile.follower_count || 0)}
            </div>
            <div className="text-sm text-neutral-400">Seguidores</div>
          </div>
          <div className="flex-1">
            <div className="text-2xl font-bold">
              {formatNumber(profile.following_count || 0)}
            </div>
            <div className="text-sm text-neutral-400">Siguiendo</div>
          </div>
        </div>

        {/* Sección de biografía */}
        <div className="mb-8">
          {profile.biography && (
            <p className="text-sm mb-3 whitespace-pre-line">
              {profile.biography}
            </p>
          )}

          {/* Enlaces externos */}
          {profile.bio_links && profile.bio_links.length > 0 && (
            <div className="mb-4">
              {(showAllBioLinks
                ? profile.bio_links
                : profile.bio_links.slice(0, 1)
              ).map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-blue-400 text-sm hover:underline mb-1"
                >
                  <Link size={14} className="mr-1" />
                  {link.title || link.url}
                  <ExternalLink size={12} className="ml-1" />
                </a>
              ))}

              {profile.bio_links.length > 1 && (
                <button
                  onClick={() => setShowAllBioLinks(!showAllBioLinks)}
                  className="text-blue-400 text-xs mt-1"
                >
                  {showAllBioLinks
                    ? "Mostrar menos"
                    : `Ver todos los ${profile.bio_links.length} enlaces`}
                </button>
              )}
            </div>
          )}

          {/* Información de contacto */}
          <div className="flex flex-wrap gap-4">
            {profile.public_email && (
              <div className="flex items-center text-sm text-neutral-300">
                <Mail size={14} className="mr-1 text-neutral-400" />
                {profile.public_email}
              </div>
            )}

            {profile.public_phone_number && (
              <div className="flex items-center text-sm text-neutral-300">
                <Phone size={14} className="mr-1 text-neutral-400" />
                {profile.public_phone_country_code &&
                  `+${profile.public_phone_country_code} `}
                {profile.public_phone_number}
              </div>
            )}

            {profile.location_data?.city_name && (
              <div className="flex items-center text-sm text-neutral-300">
                <MapPin size={14} className="mr-1 text-neutral-400" />
                {profile.location_data.city_name}
                {profile.location_data.zip && `, ${profile.location_data.zip}`}
              </div>
            )}
          </div>
        </div>

        {/* Destacados de stories */}
        {storyItems.length > 0 && (
          <div className="my-8">
            <h2 className="font-bold mb-4">Stories</h2>
            <div className="overflow-x-auto">
              <div className="flex space-x-4 pb-2">
                {storyItems.slice(0, 10).map((story, index) => (
                  <button
                    key={index}
                    onClick={() => setViewingStory(index)}
                    className="flex flex-col items-center"
                  >
                    <div className="relative w-16 h-16 mb-1">
                      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 p-0.5">
                        <div className="absolute inset-[2px] rounded-full bg-black">
                          <img
                            src={story.user?.profile_pic_url}
                            alt={story.user?.username}
                            className="rounded-full w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-neutral-400 truncate w-16 text-center">
                      {story.user?.username}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="mb-6">
          <ChatPlaceloader
            username={profile.username}
            profilePic={profile.profile_pic_url}
          />

          <div className="mt-4">
            <Button
              onClick={() => navigate("/delivery/refund")}
              variant="outline"
              className="w-full border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600 font-medium py-3"
            >
              Solicitar Reembolso
            </Button>
          </div>
        </div>

        {/* Sección de Análisis */}
        <div className="bg-neutral-900 rounded-lg p-6 my-8">
          <div className="mb-6">
            <h2 className="text-xl font-bold">Análisis del Perfil</h2>
            <span className="text-xs text-neutral-500">
              Actualizado:{" "}
              {new Date(profileData.searchedAt).toLocaleDateString()}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Distribución de Seguidores */}
            <div className="bg-neutral-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4">
                Distribución de Seguidores
              </h3>

              <div className="flex items-center">
                <div className="w-1/3 relative">
                  <div className="text-end">
                    <div className="leading-tight">
                      <span className="font-extrabold">75%</span>
                      <br />
                      <span className="font-light text-neutral-400">
                        Activos
                      </span>
                    </div>
                    <div className="h-2 w-2 bg-[#ef8d02] rounded-full ml-auto mb-1"></div>
                  </div>

                  <div className="flex relative">
                    {followersAndStories?.data?.items
                      ?.slice(0, 4)
                      .map((follower, i) => (
                        <div
                          key={i}
                          className="relative"
                          style={{ marginLeft: i > 0 ? "-8px" : "0" }}
                        >
                          <img
                            alt={follower.username}
                            className="rounded-full w-[22px] h-[22px] border border-neutral-800"
                            src={follower.profile_pic_url}
                          />
                        </div>
                      ))}
                  </div>
                </div>

                <div className="w-1/3 flex justify-center">
                  <div style={{ width: "100px", height: "100px" }}>
                    <Doughnut
                      data={followerDistributionData}
                      options={chartOptions}
                    />
                  </div>
                </div>

                <div className="w-1/3 flex">
                  <div className="text-start">
                    <div className="h-2 w-2 bg-[#02b4ef] rounded-full mt-1 mr-1"></div>
                    <div className="leading-tight">
                      <span className="font-extrabold">25%</span>
                      <br />
                      <span className="font-light text-neutral-400">
                        Inactivos
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center mt-4">
                <button
                  className="bg-neutral-700 hover:bg-neutral-600 text-white px-4 py-2 rounded-full flex items-center text-sm transition"
                  onClick={() => setStalkerModalOpen(true)}
                >
                  <Users size={16} className="mr-2" />
                  Ver seguidores
                </button>
              </div>
            </div>

            {/* Métricas de Engagement */}
            <div className="bg-neutral-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4">
                Métricas de Engagement
              </h3>
              <div style={{ height: "180px" }}>
                <Bar data={engagementData} options={engagementOptions} />
              </div>
            </div>

            {/* Stories con Actividades */}
            <div className="bg-neutral-800 rounded-lg p-4 md:col-span-2">
              <h3 className="text-lg font-semibold mb-4">
                Actividad en Stories
                <span className="text-sm font-normal text-neutral-400 ml-2">
                  ({followersWithStories} seguidores con stories activos)
                </span>
              </h3>

              <Swiper
                modules={[Navigation, Pagination]}
                spaceBetween={16}
                slidesPerView={1.5}
                pagination={{ clickable: true }}
                breakpoints={{
                  640: {
                    slidesPerView: 2.5,
                  },
                  768: {
                    slidesPerView: 3.5,
                  },
                }}
                className="mt-4"
              >
                {storyItems.map((story, index) => (
                  <SwiperSlide key={index}>
                    <StoryCard
                      count={Math.floor(Math.random() * 20) + 1}
                      action={
                        Math.random() > 0.5
                          ? "tomaron captura"
                          : "vieron +3 veces"
                      }
                      backgroundImage={
                        resolveStoryImage(story.image_versions2) ||
                        story.thumbnail_url
                      }
                      username={story.user?.username}
                      profilePic={story.user?.profile_pic_url}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Análisis de Tipo de Contenido */}
            <div className="bg-neutral-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4">Tipo de Contenido</h3>

              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span>Fotos ({photoItems.length})</span>
                    <span className="text-neutral-400">
                      {Math.round((photoItems.length / posts.length) * 100)}%
                    </span>
                  </div>
                  <div className="h-2 bg-neutral-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500"
                      style={{
                        width: `${(photoItems.length / posts.length) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-1">
                    <span>Videos ({videoItems.length})</span>
                    <span className="text-neutral-400">
                      {Math.round((videoItems.length / posts.length) * 100)}%
                    </span>
                  </div>
                  <div className="h-2 bg-neutral-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-500"
                      style={{
                        width: `${(videoItems.length / posts.length) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-1">
                    <span>Etiquetas ({taggedItems.length})</span>
                    <span className="text-neutral-400">
                      {Math.round((taggedItems.length / posts.length) * 100)}%
                    </span>
                  </div>
                  <div className="h-2 bg-neutral-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500"
                      style={{
                        width: `${(taggedItems.length / posts.length) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actividad y Alcance */}
            <div className="bg-neutral-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4">
                Alcance y Actividad
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-neutral-700 rounded-lg p-3 flex items-center">
                  <Eye className="mr-3 text-blue-400" size={24} />
                  <div>
                    <div className="text-lg font-bold">
                      {formatNumber(profile.follower_count || 0 * 0.3)}
                    </div>
                    <div className="text-xs text-neutral-400">
                      Impresiones / día
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-700 rounded-lg p-3 flex items-center">
                  <Calendar className="mr-3 text-green-400" size={24} />
                  <div>
                    <div className="text-lg font-bold">
                      {profile.total_igtv_videos || 0}
                    </div>
                    <div className="text-xs text-neutral-400">Videos IGTV</div>
                  </div>
                </div>

                <div className="bg-neutral-700 rounded-lg p-3 flex items-center">
                  <Clock className="mr-3 text-yellow-400" size={24} />
                  <div>
                    <div className="text-lg font-bold">
                      {profile.latest_reel_media
                        ? new Date(
                            profile.latest_reel_media * 1000,
                          ).toLocaleDateString()
                        : "N/A"}
                    </div>
                    <div className="text-xs text-neutral-400">Último story</div>
                  </div>
                </div>

                <div className="bg-neutral-700 rounded-lg p-3 flex items-center">
                  <UserPlus className="mr-3 text-purple-400" size={24} />
                  <div>
                    <div className="text-lg font-bold">
                      {formatNumber(
                        Math.floor((profile.follower_count || 0) * 0.08),
                      )}
                    </div>
                    <div className="text-xs text-neutral-400">
                      Nuevos seguidores/mes
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pestañas de Contenido */}
        <div className="border-t border-neutral-800 mt-8">
          <div className="flex justify-around">
            <button
              onClick={() => setActiveTab("posts")}
              className={`py-3 flex items-center justify-center flex-1 ${activeTab === "posts" ? "border-b-2 border-white" : ""}`}
            >
              <Grid
                size={20}
                className={
                  activeTab === "posts" ? "text-white" : "text-neutral-500"
                }
              />
            </button>
            <button
              onClick={() => setActiveTab("videos")}
              className={`py-3 flex items-center justify-center flex-1 ${activeTab === "videos" ? "border-b-2 border-white" : ""}`}
            >
              <Video
                size={20}
                className={
                  activeTab === "videos" ? "text-white" : "text-neutral-500"
                }
              />
            </button>
            <button
              onClick={() => setActiveTab("photos")}
              className={`py-3 flex items-center justify-center flex-1 ${activeTab === "photos" ? "border-b-2 border-white" : ""}`}
            >
              <Image
                size={20}
                className={
                  activeTab === "photos" ? "text-white" : "text-neutral-500"
                }
              />
            </button>
            <button
              onClick={() => setActiveTab("tagged")}
              className={`py-3 flex items-center justify-center flex-1 ${activeTab === "tagged" ? "border-b-2 border-white" : ""}`}
            >
              <User
                size={20}
                className={
                  activeTab === "tagged" ? "text-white" : "text-neutral-500"
                }
              />
            </button>
          </div>

          {/* Contenido de las Pestañas */}
          <div className="py-4">
            {activeTab === "posts" && (
              <div className="grid grid-cols-3 gap-1">
                {posts.map((post, index) => (
                  <div key={index} className="aspect-square relative">
                    <img
                      src={
                        resolveStoryImage(post.image_versions2) ||
                        post.thumbnail_url
                      }
                      alt={`Post ${index}`}
                      className="w-full h-full object-cover"
                    />
                    {post.media_type === 2 && (
                      <div className="absolute top-2 right-2">
                        <Play size={16} className="text-white" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 flex items-center text-xs">
                      <Heart size={12} className="mr-1" />
                      {post.like_count || Math.floor(Math.random() * 1000)}
                      <MessageCircle size={12} className="ml-2 mr-1" />
                      {post.comment_count || Math.floor(Math.random() * 100)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "videos" && (
              <div className="grid grid-cols-3 gap-1">
                {videoItems.length > 0 ? (
                  videoItems.map((video, index) => (
                    <div key={index} className="aspect-square relative">
                      <img
                        src={
                          resolveStoryImage(video.image_versions2) ||
                          video.thumbnail_url
                        }
                        alt={`Video ${index}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <Play size={20} className="text-white" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <div className="flex items-center text-xs">
                          <Heart size={12} className="mr-1" />
                          {video.like_count || Math.floor(Math.random() * 1000)}
                          <MessageCircle size={12} className="ml-2 mr-1" />
                          {video.comment_count ||
                            Math.floor(Math.random() * 100)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-3 flex flex-col items-center justify-center py-12 text-neutral-400">
                    <Video size={40} className="mb-4" />
                    <p>No se encontraron videos</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "photos" && (
              <div className="grid grid-cols-3 gap-1">
                {photoItems.length > 0 ? (
                  photoItems.map((photo, index) => (
                    <div key={index} className="aspect-square relative">
                      <img
                        src={
                          resolveStoryImage(photo.image_versions2) ||
                          photo.thumbnail_url
                        }
                        alt={`Photo ${index}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <div className="flex items-center text-xs">
                          <Heart size={12} className="mr-1" />
                          {photo.like_count || Math.floor(Math.random() * 1000)}
                          <MessageCircle size={12} className="ml-2 mr-1" />
                          {photo.comment_count ||
                            Math.floor(Math.random() * 100)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-3 flex flex-col items-center justify-center py-12 text-neutral-400">
                    <Image size={40} className="mb-4" />
                    <p>No se encontraron fotos</p>
                  </div>
                )}
              </div>
            )}
            {activeTab === "tagged" && (
              <div className="grid grid-cols-3 gap-1">
                {taggedItems.length > 0 ? (
                  taggedItems.map((tagged, index) => (
                    <div key={index} className="aspect-square relative">
                      <img
                        src={
                          resolveStoryImage(tagged.image_versions2) ||
                          tagged.thumbnail_url
                        }
                        alt={`Tagged ${index}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2">
                        <User size={14} className="text-white" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <div className="flex items-center text-xs">
                          <Heart size={12} className="mr-1" />
                          {tagged.like_count ||
                            Math.floor(Math.random() * 1000)}
                          <MessageCircle size={12} className="ml-2 mr-1" />
                          {tagged.comment_count ||
                            Math.floor(Math.random() * 100)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-3 flex flex-col items-center justify-center py-12 text-neutral-400">
                    <User size={40} className="mb-4" />
                    <p>No se encontraron publicaciones etiquetadas</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sección de Seguidores con Stories */}
        <div className="my-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Seguidores</h2>
            <button
              onClick={() => setStalkerModalOpen(true)}
              className="text-blue-400 text-sm"
            >
              Ver todos
            </button>
          </div>

          <div className="space-y-4">
            {followersAndStories?.data?.items
              ?.slice(0, 5)
              .map((follower, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="relative mr-3">
                      <img
                        src={follower.profile_pic_url}
                        alt={follower.username}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      {follower.latest_story_ts && (
                        <div
                          className="absolute inset-0 rounded-full border-2 border-pink-500"
                          style={{ padding: "2px" }}
                        ></div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center">
                        <h3 className="font-medium">{follower.full_name}</h3>
                        {follower.is_verified && (
                          <CheckCircle
                            size={14}
                            className="text-blue-500 ml-1"
                          />
                        )}
                      </div>
                      <p className="text-sm text-neutral-400">
                        @{follower.username}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {follower.latest_story_ts ? (
                      <div className="text-xs py-1 px-2 bg-pink-900/30 text-pink-400 rounded-full">
                        Story activo
                      </div>
                    ) : (
                      <div className="text-xs py-1 px-2 bg-neutral-800 text-neutral-400 rounded-full">
                        Sin story
                      </div>
                    )}
                  </div>
                </div>
              ))}

            {(followersAndStories?.data?.items?.length || 0) > 5 && (
              <button
                onClick={() => setStalkerModalOpen(true)}
                className="w-full py-3 bg-neutral-800 hover:bg-neutral-700 rounded-lg text-center text-sm font-medium transition"
              >
                Ver más seguidores
              </button>
            )}
          </div>
        </div>

        {/* Banner de Promoción Final */}
        {/* <div className="rounded-lg p-6 bg-gradient-to-r from-neutral-900 to-neutral-800 my-8">
                     <div className="flex flex-col md:flex-row items-center mb-6">
                       <div className="flex-1">
                         <h2 className="text-xl font-bold mb-2">IA Stalker Premium</h2>
                         <p className="text-sm text-neutral-300 mb-4">
                           Acceso completo a todas las estadísticas avanzadas,
                           visualizadores invisibles y análisis detallado de quién
                           interactúa más con tu perfil.
                         </p>

                         <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                           <div className="mb-4 sm:mb-0">
                             <div className="text-xs text-neutral-400">De $49,90 por</div>
                             <div className="flex items-baseline">
                               <span className="text-sm">$</span>
                               <span className="text-3xl font-bold">29,90</span>
                             </div>
                           </div>

                           <button className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white py-2 px-6 rounded-full font-bold transition">
                             Suscribirse Ahora
                           </button>
                         </div>
                       </div>
                     </div>

                     <div className="text-center text-sm text-neutral-400">
                       <div className="flex items-center justify-center">
                         <Clock size={14} className="mr-1" />
                         Oferta por tiempo limitado - expira en{" "}
                         <span className="font-bold ml-1">12:09</span>
                       </div>
                     </div>
                   </div> */}

        {/* Pie de página */}
        <div className="mt-16 mb-8 text-center">
          <p className="text-sm text-neutral-500 mb-2">
            © 2025 Teagram Analytics. Todos los derechos reservados.
          </p>
          <div className="flex justify-center space-x-4">
            <a
              href="#"
              className="text-xs text-neutral-500 hover:text-neutral-300 transition"
            >
              Términos de Uso
            </a>
            <a
              href="#"
              className="text-xs text-neutral-500 hover:text-neutral-300 transition"
            >
              Política de Privacidad
            </a>
            <a
              href="#"
              className="text-xs text-neutral-500 hover:text-neutral-300 transition"
            >
              Ayuda & Soporte
            </a>
          </div>
        </div>
      </div>

      {/* Modales */}
      <StalkerModal
        isOpen={stalkerModalOpen}
        onClose={() => setStalkerModalOpen(false)}
        followers={followersAndStories?.data?.items || []}
      />

      {/* Visor de Story */}
      {viewingStory !== null && storyItems[viewingStory] && (
        <div
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          onClick={() => setViewingStory(null)}
        >
          <div className="relative w-full max-w-lg max-h-screen overflow-hidden">
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center p-4">
              <div className="flex-1 flex overflow-x-auto space-x-2 pb-2">
                {storyItems.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-0.5 bg-white/30 flex-1 rounded-full overflow-hidden ${idx === viewingStory ? "bg-white" : ""}`}
                  >
                    {idx === viewingStory && (
                      <div className="h-full bg-white w-full animate-progress"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="absolute top-0 left-0 right-0 z-10 flex items-center p-4 pt-10">
              <div className="flex items-center">
                <img
                  src={storyItems[viewingStory].user?.profile_pic_url}
                  alt={storyItems[viewingStory].user?.username}
                  className="w-8 h-8 rounded-full mr-2"
                />
                <div>
                  <div className="text-sm font-semibold">
                    {storyItems[viewingStory].user?.username}
                  </div>
                  <div className="text-xs text-neutral-300">
                    {new Date(
                      storyItems[viewingStory].taken_at * 1000,
                    ).toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="ml-auto">
                <button
                  className="text-white"
                  onClick={() => setViewingStory(null)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>

            {storyItems[viewingStory].video_url ? (
              <video
                src={storyItems[viewingStory].video_url}
                className="w-full h-full object-contain"
                autoPlay
                muted
                loop
                playsInline
              ></video>
            ) : (
              <img
                src={
                  resolveStoryImage(storyItems[viewingStory].image_versions2) ||
                  storyItems[viewingStory].thumbnail_url
                }
                className="w-full h-full object-contain"
                alt="Story"
              />
            )}

            <div className="absolute bottom-0 left-0 right-0 p-4 flex justify-between items-center">
              <div className="flex-1 bg-white/10 rounded-full px-4 py-2">
                <input
                  type="text"
                  placeholder="Enviar mensaje"
                  className="bg-transparent w-full text-white outline-none text-sm"
                />
              </div>
              <button className="ml-2 p-2">
                <Heart size={24} />
              </button>
              <button className="p-2">
                <Share2 size={24} />
              </button>
            </div>
          </div>

          <button
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 rounded-full p-2"
            onClick={(e) => {
              e.stopPropagation();
              setViewingStory((prev) =>
                prev !== null && prev > 0 ? prev - 1 : storyItems.length - 1,
              );
            }}
          >
            <ArrowLeft size={24} />
          </button>

          <button
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 rounded-full p-2"
            onClick={(e) => {
              e.stopPropagation();
              setViewingStory((prev) =>
                prev !== null && prev < storyItems.length - 1 ? prev + 1 : 0,
              );
            }}
          >
            <ArrowLeft size={24} className="transform rotate-180" />
          </button>
        </div>
      )}

      {/* Floating Support Component */}
      <FloatingSupport />
    </div>
  );
}
